import React from 'react';
import Select from 'react-select';

const STYLE_ID = 'tpl-field-styles';

// Injected once, globally, instead of a <style> tag per row instance.
const injectStyles = () => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .tpl-field-wrap { 
      min-height: 42px; 
      background: #fff; 
      resize: vertical;
      overflow: auto;
      max-height: 300px;
    }
    .tpl-field-wrap:focus-within { border-color: #86b7fe; box-shadow: 0 0 0 0.2rem rgba(13,110,253,.15); }
    .tpl-editor { min-height: 26px; outline: none; line-height: 2; word-break: break-word; }
    .tpl-token {
      display: inline-block; background: #7d7414; color: #fff; font-weight: 600;
      font-size: 0.85em; border-radius: 4px; padding: 1px 4px 1px 8px; margin: 1px 2px;
      white-space: nowrap; user-select: none;
    }
    .tpl-token-remove {
      display: inline-block; margin-left: 6px; padding: 0 3px; cursor: pointer;
      opacity: 0.75; border-radius: 3px;
    }
    .tpl-token-remove:hover { opacity: 1; background: rgba(255,255,255,0.25); }
    .tpl-placeholder { top: 10px; left: 12px; pointer-events: none; z-index: 0; }
  `;
  document.head.appendChild(style);
};

/**
 * Value shape (unchanged):
 * parts = [{ t: 'f', k }, { t: 't', v }, ...]
 *
 * Interaction:
 * - click inside template input => opens Facebook field popup
 * - typing free text closes popup
 * - click outside closes popup
 * - token insert still happens at cursor position
 */
class TemplateField extends React.Component {
  constructor(props) {
    super(props);
    injectStyles();

    this.editorRef = React.createRef();
    this.wrapperRef = React.createRef();
    this.popoverRef = React.createRef();

    this.savedRange = null;
    this.lastEmittedJSON = JSON.stringify(props.parts || []);

    this.state = {
      showPicker: false
    };
  }

  componentDidMount() {
    this.renderDom(this.props.parts);
    document.addEventListener('mousedown', this.handleOutsideClick);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick);
  }

  componentDidUpdate() {
    const incomingJSON = JSON.stringify(this.props.parts || []);
    if (incomingJSON !== this.lastEmittedJSON) {
      this.renderDom(this.props.parts);
      this.lastEmittedJSON = incomingJSON;
    }
  }

  // ---------- helpers ----------

  isPickerDisabled = () => {
    return this.props.singleTokenOnly && this.parseDom().some(p => p.t === 'f');
  };

  handleOutsideClick = (e) => {
    if (this.wrapperRef.current && !this.wrapperRef.current.contains(e.target)) {
      if (this.state.showPicker) {
        this.setState({ showPicker: false });
      }
    }
  };

  // ---------- DOM <-> parts ----------

  renderDom = (parts) => {
    const el = this.editorRef.current;
    if (!el) return;

    el.innerHTML = '';
    (parts || []).forEach(p => {
      el.appendChild(p.t === 'f' ? this.makeTokenNode(p.k) : document.createTextNode(p.v || ''));
    });

    if (!el.childNodes.length) el.appendChild(document.createTextNode(''));
  };

  makeTokenNode = (key) => {
    const span = document.createElement('span');
    span.className = 'tpl-token';
    span.contentEditable = 'false';
    span.dataset.key = key;

    const label = document.createElement('span');
    label.textContent = this.labelFor(key);
    span.appendChild(label);

    const remove = document.createElement('span');
    remove.className = 'tpl-token-remove';
    remove.textContent = '\u00D7';
    span.appendChild(remove);

    return span;
  };

  labelFor = (k) => {
    const flat = (this.props.fieldOptions || []).reduce((acc, g) => acc.concat(g.options), []);
    const found = flat.find(o => o.value === k);
    return found ? found.label.replace(/\s*\(.*\)$/, '') : `\u26A0 ${k}`;
  };

  parseDom = () => {
    const el = this.editorRef.current;
    if (!el) return [];

    const parts = [];
    el.childNodes.forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('tpl-token')) {
        parts.push({ t: 'f', k: node.dataset.key });
        return;
      }

      const v = node.textContent;
      if (parts.length && parts[parts.length - 1].t === 't') {
        parts[parts.length - 1].v += v;
      } else {
        parts.push({ t: 't', v });
      }
    });

    return parts.filter(p => !(p.t === 't' && p.v === ''));
  };

  emit = () => {
    const parts = this.parseDom();
    this.lastEmittedJSON = JSON.stringify(parts);
    this.props.onChange(parts);
  };

  // ---------- selection tracking ----------

  saveSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    if (this.editorRef.current && this.editorRef.current.contains(range.commonAncestorContainer)) {
      this.savedRange = range.cloneRange();
    }
  };

  rangeInEditor = () => {
    if (
      this.savedRange &&
      this.editorRef.current &&
      this.editorRef.current.contains(this.savedRange.commonAncestorContainer)
    ) {
      return this.savedRange.cloneRange();
    }
    return null;
  };

  endOfEditorRange = () => {
    const range = document.createRange();
    range.selectNodeContents(this.editorRef.current);
    range.collapse(false);
    return range;
  };

  // ---------- events ----------

  handleInput = () => {
    this.emit();

    // If the user starts typing, assume they want text, not the popup.
    if (this.state.showPicker) {
      this.setState({ showPicker: false });
    }
  };

  handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  handleKeyDown = (e) => {
    if (this.props.singleTokenOnly) {
      const hasToken = this.parseDom().some(p => p.t === 'f');
      if (hasToken && e.key.length === 1) {
        e.preventDefault();
      }
    }
  };

  handleWrapperClick = (e) => {
    const removeBtn = e.target && e.target.closest ? e.target.closest('.tpl-token-remove') : null;

    // Token remove click should NOT open the popup.
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();

      const token = removeBtn.closest('.tpl-token');
      if (token) token.remove();

      this.emit();
      return;
    }

    // Clicks inside the opened popup should not re-trigger wrapper behavior.
    if (this.popoverRef.current && this.popoverRef.current.contains(e.target)) {
      return;
    }

    if (this.isPickerDisabled()) return;

    const sel = window.getSelection();

    // If user is actively selecting text, don't interrupt by opening popup.
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) return;

    const editor = this.editorRef.current;
    if (!editor) return;

    // If click happened on wrapper padding / placeholder area,
    // focus editor at end and open popup.
    if (!editor.contains(e.target)) {
      editor.focus();

      const range = this.endOfEditorRange();
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      this.savedRange = range.cloneRange();
    } else {
      // Click inside editor: preserve cursor position.
      this.saveSelection();
    }

    this.setState({ showPicker: true });
  };

  insertToken = (option) => {
    if (!option) return;

    const el = this.editorRef.current;

    if (this.props.singleTokenOnly) {
      el.innerHTML = '';
      el.appendChild(this.makeTokenNode(option.value));
      this.setState({ showPicker: false });
      this.emit();
      return;
    }

    el.focus();

    const range = this.rangeInEditor() || this.endOfEditorRange();
    const token = this.makeTokenNode(option.value);

    range.deleteContents();
    range.insertNode(token);

    const spacer = document.createTextNode(' ');
    token.after(spacer);

    const newRange = document.createRange();
    newRange.setStartAfter(spacer);
    newRange.collapse(true);

    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(newRange);

    this.savedRange = newRange.cloneRange();

    this.setState({ showPicker: false });
    this.emit();
  };

  render() {
    const { fieldOptions, placeholder, singleTokenOnly, parts } = this.props;
    const isEmpty = !(parts || []).length;
    const disablePicker = singleTokenOnly && (parts || []).some(p => p.t === 'f');

    return (
      <div className="position-relative" ref={this.wrapperRef}>
        <div
          className="tpl-field-wrap border rounded px-2 py-1 position-relative"
          style={{ cursor: disablePicker ? 'not-allowed' : 'text' }}
          onClick={this.handleWrapperClick}
        >
          {isEmpty && (
            <span className="tpl-placeholder text-muted small position-absolute">
              {placeholder}
            </span>
          )}

          <div
            ref={this.editorRef}
            className="tpl-editor"
            contentEditable
            suppressContentEditableWarning
            onInput={this.handleInput}
            onPaste={this.handlePaste}
            onKeyDown={this.handleKeyDown}
            onKeyUp={this.saveSelection}
            onMouseUp={this.saveSelection}
            onBlur={this.saveSelection}
          />
        </div>

        {disablePicker && (
          <span className="text-muted small d-block mt-1">
            Date fields accept a single Facebook field only
          </span>
        )}

        {this.state.showPicker && !disablePicker && (
          <div
            ref={this.popoverRef}
            className="position-absolute w-100 mt-1"
            style={{ zIndex: 1000 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Select
              autoFocus
              value={null}
              onChange={this.insertToken}
              options={fieldOptions}
              isSearchable
              placeholder="Select Facebook field..."
              menuIsOpen={true}
            />
          </div>
        )}
      </div>
    );
  }
}

export default TemplateField;